import { css } from '@/styled-system/css';
import { Box, Heading, IconButton } from '@zillow/constellation-10';
import { IconChevronLeftFilled, IconUserOutline } from '@zillow/constellation-icons';
import { AvatarGroup, ConversationActions, ReportConversationModal } from '@zg-rentals/satellite-components';
import { useContext } from 'react';
import { MultiBrandSupportUserReportConversationReasonType } from '@zg-rentals/ts-satellite-client';
import { InboxPageMobileView } from '../../types/InboxPageMobileView';
import {
  emitRenterProfileMobileIconClickEvent,
  RENTER_PROFILE_MOBILE_ICON_CLICK_EVENT_TYPE_ID,
} from '../../constants/clickstreamEvents/renterProfile';
import { preventMarkAsReadStore } from '../../lib/inboxStores';
import { inboxPageShowEmptyContainerStore } from '../../lib/inboxSearchListStores';
import useConversationActions from '../../hooks/useConversationActions';
import useDevices from '../../hooks/useDevices';
import useInboxRouterHelper from '../../hooks/useInboxRouterHelper';
import { useConversationMessagesQuery } from '../../hooks/useConversationMessagesQuery';
import type { ConversationMessagesData } from '../../utils/conversationMessageUtils';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import conversationUtils from '../../utils/conversationUtils';
import getActiveParticipantNames from '../../utils/getActiveParticipantNames';
import ConversationNavSkeleton from './skeleton';
import { FULLSTORY_EXCLUDE_CLASS_NAME } from '@zg-rentals/satellite-components';
import useModal from '../../hooks/useModal';
import { ModalType } from '../../types/ModalType';
import { AppContext } from '../../contexts/AppContextProvider';
import { emitOpenReportProblemModalEvent } from '../../constants/clickstreamEvents/reportProblemModal';
import { TRIGGER_LOCATION } from '../../constants/clickstreamEvents/globals';
import { useAuth } from '../../AuthProvider';

export type ConversationNavData = {
  activeParticipants: ConversationMessagesData['activeParticipants'];
  isPhoneLead: boolean;
  renterName: string;
  renterPhone: string;
  isConversationBlockedByZillow: boolean;
  status: ConversationMessagesData['status'];
};

export const selectConversationNavData = (data: ConversationMessagesData): ConversationNavData => ({
  activeParticipants: data.activeParticipants,
  isPhoneLead: data.isPhoneLead,
  renterName: data.renterName ?? '',
  renterPhone: data.renterPhone ?? '',
  isConversationBlockedByZillow: data.isConversationBlockedByZillow,
  status: data.status,
});

export const conversationNavWrapperStyles = css.raw({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  paddingBlock: 'default',
  borderBottom: '1px solid',
  // oxlint-disable-next-line constellation/prefer-semantic-tokens -- pre-existing base token; ESLint treated this as warn-level, not blocking
  borderBottomColor: 'gray.300',
  height: '2000',
  xlDown: {
    paddingLeft: 'tightest',
  },
  xxl: {
    paddingInline: 'default',
  },
});

const ConversationNav = (): JSX.Element | null => {
  const { isImpersonator } = useContext(AppContext);
  const { conversationId, listingAlias, clientTimeZone } = useContext(InboxPageContext);
  const { user } = useAuth();
  // Cache-only read — `MessageListItems` owns the `getMessages` fetch + polling.
  const { data: conversationNavData } = useConversationMessagesQuery({
    conversationId,
    clientTimeZone,
    enabled: false,
    select: selectConversationNavData,
  });
  const inboxPageShowEmptyContainer = inboxPageShowEmptyContainerStore.useValue();
  const { changeMobileView } = useInboxRouterHelper();
  const { isMobileView } = useDevices();
  const { activeModal, clearModal, setActiveModal } = useModal();
  const {
    archiveConversation,
    markConversationAsUnread,
    reportConversation,
    undoArchiveConversation,
    undoReportConversation,
  } = useConversationActions();

  if (!conversationNavData) {
    return <ConversationNavSkeleton />;
  }

  if (inboxPageShowEmptyContainer) {
    return null;
  }

  const { activeParticipants, isPhoneLead, renterName, renterPhone, isConversationBlockedByZillow, status } =
    conversationNavData;

  const { displayNames, fullParticipantList } = getActiveParticipantNames({
    activeParticipantsInConversation: activeParticipants,
    referenceName: renterName,
  });

  const displayName = isPhoneLead
    ? conversationUtils.getPhoneLeadParticipantDisplay({
        name: renterName,
        phoneNumber: renterPhone,
        shouldFormatPhoneNumber: true,
        formattedPhoneNumberPrefix: 'Renter at ',
      })
    : displayNames;

  const handleRenterProfileClick = () => {
    emitRenterProfileMobileIconClickEvent(user);
    changeMobileView(InboxPageMobileView.RenterProfile);
  };

  const handleBackArrowClick = () => {
    changeMobileView(InboxPageMobileView.ConversationList);
  };

  const handleUndoReportConversation = () => {
    return undoReportConversation({
      conversationIdToUpdate: conversationId!,
      reportReasonType: MultiBrandSupportUserReportConversationReasonType.NOT_REPORTED,
    });
  };

  const handleMarkConversationAsUnread = () => {
    markConversationAsUnread({
      conversationIdToUpdate: conversationId,
    });

    // Gmail-style: on mobile, marking a conversation unread closes the message view and returns
    // to the conversation list, so the unread state is immediately visible (RCRM-6577). Re-opening
    // the conversation and navigating back then marks it read again.
    if (isMobileView) {
      // Set the suppression flag synchronously here — `markConversationAsUnread` only sets it in
      // its async `onSuccess`, so without this the back-nav mark-read hook that fires on this
      // navigation could race ahead and undo the mark-unread when the conversation is currently
      // unread (e.g. a new message arrived while scrolled up).
      preventMarkAsReadStore.set(true);
      changeMobileView(InboxPageMobileView.ConversationList);
    }
  };

  const handleOpenReportConversationModal = () => {
    emitOpenReportProblemModalEvent({ user, listing: { listingAlias }, triggerLocation: TRIGGER_LOCATION.IreneInbox });
    setActiveModal(ModalType.ReportConversationModal);
  };

  const viewRenterProfileButton = (
    <IconButton
      data-eventid={RENTER_PROFILE_MOBILE_ICON_CLICK_EVENT_TYPE_ID}
      onClick={handleRenterProfileClick}
      emphasis="bare"
      icon={<IconUserOutline />}
      shape="square"
      size="md"
      title="View renter profile"
      tone="brand"
      css={{
        xxl: {
          display: 'none',
        },
      }}
    />
  );

  return (
    <>
      {activeModal === ModalType.ReportConversationModal && (
        <ReportConversationModal
          isImpersonator={isImpersonator}
          name={displayName}
          onCancelClick={clearModal}
          onReportClick={({ reason, comments }) =>
            reportConversation({
              conversationIdToUpdate: conversationId!,
              reportReasonType: reason,
              userReportComment: comments,
            })
          }
        />
      )}

      <Box
        data-testid="conversation-nav"
        aria-label={`Chat window for conversation with ${displayName}`}
        css={conversationNavWrapperStyles}
      >
        <IconButton
          onClick={handleBackArrowClick}
          emphasis="bare"
          icon={<IconChevronLeftFilled />}
          shape="square"
          size="md"
          title="Go back to conversation list"
          tone="brand"
          css={{
            marginRight: 'default',
            xxl: {
              display: 'none',
            },
          }}
        />

        <AvatarGroup participants={fullParticipantList} variant="default" />

        <Heading
          level={2}
          textStyle="body-lg-bold"
          css={{
            truncate: true,
            width: '100%',
            maxWidth: '100%',
            paddingLeft: 'default',
            textTransform: 'capitalize',
          }}
          className={FULLSTORY_EXCLUDE_CLASS_NAME}
        >
          {displayName}
        </Heading>

        <ConversationActions
          actions={{
            archiveConversation,
            markConversationAsUnread: handleMarkConversationAsUnread,
            openReportConversationModal: handleOpenReportConversationModal,
            undoArchiveConversation,
            undoReportConversation: handleUndoReportConversation,
          }}
          additionalActionButton={viewRenterProfileButton}
          breakpoint="xxl"
          isBlocked={isConversationBlockedByZillow}
          isImpersonator={isImpersonator}
          shouldShowMarkUnread={true}
          status={status}
        />
      </Box>
    </>
  );
};

export default ConversationNav;
